import { AxiosError } from 'axios'
import type { ReasonCode } from '@shared/domain/types'

export interface ApiErrorBody {
  code: string
  message: string
  reasons?: ReasonCode[]
  /** backend validation errors: field → message */
  details?: Record<string, unknown> | null
  correlationId?: string
}

export type AppErrorInfo =
  | { kind: 'validation'; reasons: ReasonCode[]; correlationId?: string } // 400/422 → form fields
  | { kind: 'unauthorized' } // 401 after refresh failed
  | { kind: 'forbidden'; correlationId?: string } // 403
  | { kind: 'notFound'; correlationId?: string } // 404
  | { kind: 'conflict'; code: string; message: string; correlationId?: string } // 409, e.g. INVOICE_ALREADY_LOCKED
  | { kind: 'server'; correlationId?: string } // 5xx → toast with correlation id
  | { kind: 'network' }

export type AppErrorKind = AppErrorInfo['kind']

/** The only error type components and composables ever see from the API layer. */
export class AppError extends Error {
  readonly info: AppErrorInfo
  readonly status?: number

  constructor(info: AppErrorInfo, status?: number) {
    super(info.kind)
    this.name = 'AppError'
    this.info = info
    this.status = status
  }

  get kind(): AppErrorKind {
    return this.info.kind
  }

  get correlationId(): string | undefined {
    return 'correlationId' in this.info ? this.info.correlationId : undefined
  }
}

export function isAppError(e: unknown): e is AppError {
  return e instanceof AppError
}

export function isConflict(e: unknown, code?: string): e is AppError {
  return isAppError(e) && e.info.kind === 'conflict' && (!code || e.info.code === code)
}

function isApiErrorBody(v: unknown): v is ApiErrorBody {
  return !!v && typeof v === 'object' && 'code' in v && 'message' in v
}

/** Accepts both `{ code, message, reasons }` and the envelope `{ success: false, error: {…} }`. */
function readErrorBody(data: unknown): ApiErrorBody | undefined {
  const inner =
    data && typeof data === 'object' && 'error' in data && typeof data.error === 'object'
      ? data.error
      : data
  if (!isApiErrorBody(inner)) return undefined
  if (inner.reasons || !inner.details) return inner
  const reasons = Object.entries(inner.details).map(([field, message]) => ({
    code: inner.code,
    message: String(message),
    field,
  }))
  return { ...inner, reasons }
}

export function toAppError(error: unknown): AppError {
  if (isAppError(error)) return error
  if (!(error instanceof AxiosError)) return new AppError({ kind: 'network' })

  const res = error.response
  if (!res) return new AppError({ kind: 'network' })

  const body = readErrorBody(res.data)
  const headerId = res.headers?.['x-correlation-id'] as string | undefined
  const sentId = error.config?.headers?.['X-Correlation-Id'] as string | undefined
  const correlationId = body?.correlationId ?? headerId ?? sentId

  switch (res.status) {
    case 400:
    case 422:
      return new AppError(
        {
          kind: 'validation',
          reasons: body?.reasons ?? (body ? [{ code: body.code, message: body.message }] : []),
          correlationId,
        },
        res.status,
      )
    case 401:
      return new AppError({ kind: 'unauthorized' }, 401)
    case 403:
      return new AppError({ kind: 'forbidden', correlationId }, 403)
    case 404:
      return new AppError({ kind: 'notFound', correlationId }, 404)
    case 409:
      return new AppError(
        {
          kind: 'conflict',
          code: body?.code ?? 'CONFLICT',
          message: body?.message ?? '',
          correlationId,
        },
        409,
      )
    default:
      return new AppError({ kind: 'server', correlationId }, res.status)
  }
}

/** Split validation reasons into per-field errors (VeeValidate) and a summary banner. */
export function splitReasons(reasons: ReasonCode[]): {
  fieldErrors: Record<string, string>
  general: ReasonCode[]
} {
  const fieldErrors: Record<string, string> = {}
  const general: ReasonCode[] = []
  for (const r of reasons) {
    if (r.field && !(r.field in fieldErrors)) fieldErrors[r.field] = r.message
    else if (!r.field) general.push(r)
  }
  return { fieldErrors, general }
}
